import React, { useMemo, useState } from 'react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function daysInMonth(year, month) {
  // month is 1-12
  return new Date(year, month, 0).getDate();
}

/**
 * A cascading date-of-birth picker: only the Year dropdown is shown at
 * first. Once a year is picked, the Month dropdown appears. Once a month
 * is picked, the Day dropdown appears (with the correct number of days
 * for that month/year, handling leap years).
 *
 * Keeps its own internal year/month/day state so a partial selection
 * (e.g. year picked but not month yet) stays visible on screen even
 * though the parent form only receives a value once all three are set.
 *
 * value: ISO date string 'YYYY-MM-DD' (used only to seed the initial state).
 * onChange(isoString): called with the full ISO string once all three
 * parts are selected, or '' while the selection is still incomplete.
 */
export default function DateOfBirthPicker({ value, onChange }) {
  const initial = value ? value.split('-') : ['', '', ''];
  const [year, setYear] = useState(initial[0] || '');
  const [month, setMonth] = useState(initial[1] ? String(Number(initial[1])) : '');
  const [day, setDay] = useState(initial[2] ? String(Number(initial[2])) : '');

  const currentYear = new Date().getFullYear();
  // Reasonable range for a workforce membership site: 16 to 100 years old
  const years = useMemo(() => {
    const list = [];
    for (let y = currentYear - 16; y >= currentYear - 100; y--) {
      list.push(y);
    }
    return list;
  }, [currentYear]);

  const dayOptions = useMemo(() => {
    if (!year || !month) return [];
    const total = daysInMonth(Number(year), Number(month));
    return Array.from({ length: total }, (_, i) => i + 1);
  }, [year, month]);

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setYear(newYear);
    setMonth('');
    setDay('');
    onChange(''); // incomplete until month + day are also chosen
  };

  const handleMonthChange = (e) => {
    const newMonth = e.target.value;
    setMonth(newMonth);
    setDay('');
    onChange(''); // incomplete until day is also chosen
  };

  const handleDayChange = (e) => {
    const newDay = e.target.value;
    setDay(newDay);
    if (year && month && newDay) {
      const mm = String(month).padStart(2, '0');
      const dd = String(newDay).padStart(2, '0');
      onChange(`${year}-${mm}-${dd}`);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <select value={year} onChange={handleYearChange} style={{ flex: 1.2 }}>
        <option value="">Year</option>
        {years.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>

      {year && (
        <select value={month} onChange={handleMonthChange} style={{ flex: 1.6 }}>
          <option value="">Month</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </select>
      )}

      {year && month && (
        <select value={day} onChange={handleDayChange} style={{ flex: 1 }}>
          <option value="">Day</option>
          {dayOptions.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      )}
    </div>
  );
}
